import React, { useState } from 'react'
import Panel from 'components/layout/Panel'
import { Button, Container, hooks, Icon, parsers, Spacer, Text } from 'roadie-ui'
import RateMeter from './RateMeter'
import { LocalizedText } from 'localization'

type MessageBand = 'green' | 'yellow' | 'red'

export type RateMessages = Record<MessageBand, { title: string; body: string }>

type RateMeterSectionProps = {
  score: number
  description: string
  messages: RateMessages
  yellowMin: number
  greenMin: number
  RangesModal: React.ComponentType<{ open: boolean; onClose: () => void }>
}

function getMessageBand(score: number, yellowMin: number, greenMin: number): MessageBand {
  if (score >= greenMin) return 'green'
  if (score >= yellowMin) return 'yellow'
  return 'red'
}

const RateMessage: React.FC<{ score: number; messages: RateMessages; yellowMin: number; greenMin: number }> = ({
  score,
  messages,
  yellowMin,
  greenMin,
}) => {
  const { title, body } = messages[getMessageBand(score, yellowMin, greenMin)]
  const colorScheme = hooks.usePreferredColorScheme()
  const messageBackground =
    colorScheme === 'dark'
      ? parsers.surfaceColor.modify('primary-lighter', parsers.baselineColor.modifiers.opacity(0.35))
      : parsers.surfaceColor.modify('neutral-light', parsers.baselineColor.modifiers.opacity(0.2))

  return (
    <Container.Base $backgroundColor={messageBackground} $padding="1.6rem" $borderRadius="1.2rem" $textAlign="left">
      <Text.Body1 $fontWeight="bold" $color="neutral-white">
        {title}
      </Text.Body1>
      <Spacer $height="0.8rem" />
      <Text.Body2 $color="neutral-white">{body}</Text.Body2>
    </Container.Base>
  )
}

const RateMeterSection: React.FC<RateMeterSectionProps> = ({
  score,
  description,
  messages,
  yellowMin,
  greenMin,
  RangesModal,
}) => {
  const [modalOpen, setModalOpen] = useState(false)

  return (
    <>
      <Panel noPadding>
        <Container.Base $backgroundColor="primary-darker" $padding="2.4rem 2.4rem 2rem" $textAlign="center">
          <Container.Position $position="absolute" $top="1.2rem" $right="1.2rem">
            <Button.Tertiary type="button" onClick={() => setModalOpen(true)} style={{ padding: 0, minWidth: 0 }}>
              <Icon.QuestionMarkCircle $color="neutral-white" $width="2.4rem" $height="2.4rem" />
            </Button.Tertiary>
          </Container.Position>
          <RateMeter score={score} yellowMin={yellowMin} greenMin={greenMin} />
          <Spacer $height="0.8rem" />
          <Text.Body2 $color="neutral-white">{description}</Text.Body2>
          <Spacer $height="0.4rem" />
          <Text.Caption $color="neutral-white">
            <LocalizedText translationKey="dweb_dashboard_rate_meter_section_updates_daily" />
          </Text.Caption>
          <Spacer $height="1.6rem" />
          <RateMessage score={score} messages={messages} yellowMin={yellowMin} greenMin={greenMin} />
        </Container.Base>
      </Panel>
      <RangesModal open={modalOpen} onClose={() => setModalOpen(false)} />
    </>
  )
}

export default RateMeterSection
